import { styled } from '@mui/material';
import classNames from 'classnames';
import { MouseEvent, type Ref, TouchEvent, useId } from 'react';

import { AIRCRAFT_SVG_SIZE, AircraftIcon, type Point } from '#shared';

import { EZLayers, ZLayer } from '../../data-context';
import { useGeoMap } from '../../GeoMap';
import { parseDonorReferencePrimary } from '../../helpers';

import type { AircraftData } from '#entities';

const PREFIX = 'TSMED-AircraftOnFlight';
const aircraftOnFlightClasses = {
  wrapper: `${PREFIX}-wrapper`,
  icon: `${PREFIX}-icon`,
  timeMark: `${PREFIX}-aircraftTimeMark`,
  timeMarkBase: `${PREFIX}-aircraftTimeMarkBase`,
  registration: `${PREFIX}-aircraftRegistration`,
  caseLabel: `${PREFIX}-aircraftCaseLabel`,
  details: `${PREFIX}-details`,
  airport: `${PREFIX}-aircraftAirport`,
  airportDeparture: `${PREFIX}-aircraftAirportDeparture`,
  airportArrival: `${PREFIX}-aircraftAirportArrival`,
  trail: `${PREFIX}-trail`,
};

const Root = styled('g')(({ theme: { palette, mixins, utils } }) => ({
  ...mixins.severityStyles(palette),

  cursor: 'pointer',
  color: 'var(--primaryColor)',

  [`.${aircraftOnFlightClasses.icon}`]: {
    filter:
      'brightness(0.55) saturate(0.8) drop-shadow(0 32px 2px rgba(0, 0, 0, 0.3))',
    fill: 'currentColor',
    path: {
      transition: 'all 0.3s ease-in-out',
      fill: 'inherit',
    },
  },

  [`.${aircraftOnFlightClasses.registration}`]: {
    fill: 'currentColor',
    color: 'inherit',
    textAnchor: 'middle',
    transition: 'all 0.3s ease-in-out',
    fontSize: utils.clamp(12, 14),
    transform: 'translate(14px, 30px)',
    textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
    strokeWidth: 2,
    stroke: 'black',
    paintOrder: 'stroke',
  },

  [`.${aircraftOnFlightClasses.details}`]: {
    display: 'block',
    opacity: 0,
    visibility: 'hidden',
    pointerEvents: 'none',
    transition: 'opacity 250ms ease-in-out, visibility 0s linear 250ms',
    text: {
      textShadow: '0px 5.081px 5.081px rgba(0, 0, 0, 0.30)',
      strokeWidth: 2,
      stroke: 'black',
      paintOrder: 'stroke',
    },
  },
  [`&:hover`]: {
    [`.${aircraftOnFlightClasses.details}`]: {
      opacity: 1,
      visibility: 'visible',
      pointerEvents: 'auto',
      transition: 'opacity 250ms ease-in-out, visibility 0s',
    },
    [`.${aircraftOnFlightClasses.icon}`]: {
      filter:
        'brightness(1) saturate(1) drop-shadow(0 32px 2px rgba(0, 0, 0, 0.3))',
      path: {
        strokeWidth: 16,
        stroke: 'black',
        paintOrder: 'stroke',
      },
    },
  },
  [`&`]: {
    pointerEvents: 'visiblePainted',
  },

  [`.${aircraftOnFlightClasses.trail}`]: {
    fill: 'none',
    strokeWidth: 1.5,
    opacity: 0,
    transition: 'opacity 250ms ease-in-out',
    [`&.highlighted`]: { opacity: 1 },
    [`&.organ-type-heart`]: { stroke: 'var(--heartColor)' },
    [`&.organ-type-lungs`]: { stroke: 'var(--lungsColor)' },
    [`&.organ-type-liver`]: { stroke: 'var(--liverColor)' },
  },
}));

export interface AviationAircraftProps {
  aircraftData: AircraftData;
  handleHighlightAircraft?: (registration?: string) => void;
  onAircraftSelect?: (aircraftId: number) => void;
  ref?: Ref<SVGGElement>;
}

export const AircraftOnFlight = ({
  aircraftData,
  handleHighlightAircraft,
  onAircraftSelect,
  ref,
}: AviationAircraftProps) => {
  const { projectionRef } = useGeoMap();
  const { Location, actualLabel, id, registration, actualOrgan } = aircraftData;
  const gradientId = useId().replace(/:/g, '');
  const trailGradientId = `trailGradient-${gradientId}`;
  const maskId = `trailMask-${gradientId}`;

  if (!(Location.Latitude && Location.Longitude)) {
    return null;
  }

  const { organ, unosId, organShort } = parseDonorReferencePrimary(actualLabel);
  // Reading projection ref during render is intentional for D3/SVG coordinate transformation
  // The projection function is stable and required to compute render positions

  const [x, y] = projectionRef.current([
    Location.Longitude,
    Location.Latitude,
  ]) as Point;

  const tx = x - AIRCRAFT_SVG_SIZE / 2;
  const ty = y - AIRCRAFT_SVG_SIZE / 2;
  const headingDeg = (Location.Heading ?? 0) - 90;

  const handleSelectAircraft = (
    e: MouseEvent<SVGGElement> | TouchEvent<SVGGElement>,
    aircraftId: number,
  ) => {
    e.stopPropagation();
    onAircraftSelect?.(aircraftId);
  };

  return (
    <>
      <defs>
        {/* Create a mask that fades out */}
        <linearGradient
          className={`organ-type-${actualOrgan}`}
          id={trailGradientId}
          gradientUnits="objectBoundingBox"
          x1="0"
          y1="0"
          x2="1"
          y2="0"
        >
          <stop offset="0" stopColor={`var(--primaryColor)`} stopOpacity="1" />
          <stop offset="0.3" stopColor="white" stopOpacity="0" />
        </linearGradient>

        <mask id={maskId}>
          <rect
            x="0"
            y="0"
            width="100%"
            height="100%"
            fill={`url(#${trailGradientId})`}
          />
        </mask>
      </defs>
      <ZLayer zIndex={EZLayers.Highlighted}>
        <Root
          className={classNames('aircraft-on-flight', {
            [`organ-type-${organ}`]: organ,
            [`case-${unosId}-${organShort}`]: organShort && unosId,
          })}
          ref={ref}
          key={id}
          transform={`translate(${tx}, ${ty})`}
          onClick={e => handleSelectAircraft(e, id)}
          onTouchEnd={e => handleSelectAircraft(e, id)}
          onMouseEnter={() => handleHighlightAircraft?.(registration)}
          onMouseLeave={() => handleHighlightAircraft?.()}
        >
          <g
            transform={`rotate(${headingDeg} ${AIRCRAFT_SVG_SIZE / 2} ${
              AIRCRAFT_SVG_SIZE / 2
            })`}
          >
            <AircraftIcon
              height={AIRCRAFT_SVG_SIZE}
              width={AIRCRAFT_SVG_SIZE}
              className={aircraftOnFlightClasses.icon}
            />
          </g>

          <g className={aircraftOnFlightClasses.details}>
            <text x={0} y={10} className={aircraftOnFlightClasses.registration}>
              {registration}
            </text>
          </g>
        </Root>
      </ZLayer>
    </>
  );
};
